import { useState, useEffect, forwardRef } from "react";
import { CircleCheck, XCircle, Eye, EyeOff } from "lucide-react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

const cn = (...inputs) => twMerge(clsx(inputs));

const CommonInput = forwardRef(
  (
    {
      required = false,
      defaultValue,
      value,
      placeholder,
      onChange,
      className = "",
      icon = null,
      rightIcon = null,
      showRightIcon = "always", // "always" | "hover" | "condition"
      showRightIconCondition = false,
      type = "text",
      disabled = false,
      name,
      editable = true,
      inputClassName = "",
      wrapperClassName = "",
      variant = "box", // "box" | "underline"
      ...rest
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const [inputValue, setInputValue] = useState(defaultValue || "");
    const [isHovered, setIsHovered] = useState(false);
    const isPassword = type === "password";
    const [showPassword, setShowPassword] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(null);

    const {
      onChange: registerOnChange,
      onBlur: registerOnBlur,
      ...otherRest
    } = rest || {};

    const currentValue = isControlled ? value : inputValue;

    useEffect(() => {
      if (type === "email") {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentValue);
        setIsValidEmail(currentValue ? isValid : null);
      }
    }, [currentValue, type]);

    const handleChange = (e) => {
      if (disabled) return;

      if (!isControlled) {
        setInputValue(e.target.value);
      }
      onChange?.(e);
      registerOnChange?.(e);
    };

    const handleBlur = (e) => {
      registerOnBlur?.(e);
    };

    const EmailValidationIcon = () => {
      if (type === "email" && currentValue) {
        return isValidEmail ? (
          <CircleCheck
            className={cn(
              "w-[18px] h-[18px]",
              disabled ? "text-gray-300" : "text-white",
            )}
            fill={disabled ? "#d1d5db" : "#207c97"}
          />
        ) : (
          <XCircle
            className={cn(
              "w-4 h-4",
              disabled ? "text-gray-300" : "text-red-500",
            )}
          />
        );
      }
      return null;
    };

    const shouldShowRightIcon =
      !disabled &&
      (showRightIcon === "always" ||
        (showRightIcon === "hover" && isHovered) ||
        (showRightIcon === "condition" && showRightIconCondition));

    return (
      <div
        className={cn(
          variant === "underline" ? "relative w-5/6" : "relative min-w-[212px]",
          wrapperClassName
        )}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => !disabled && setIsHovered(false)}
      >
        <div
          className={cn(
            "flex items-center transition-colors",
            variant === "underline"
              ? "w-full border-b-1.5 p-3 mb-3.5 focus-within:border-b-secondary focus-within:border-b-[1.5px] dark:border-gray-700"
              : "rounded-lg px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900/80 shadow-sm focus-within:border-secondary focus-within:border-[1.5px]",
            icon && "pl-9",
            disabled
              ? variant === "underline"
                ? "border-gray-200 dark:border-gray-800 cursor-not-allowed opacity-70"
                : "bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800 cursor-not-allowed opacity-70"
              : variant === "underline"
                ? "border-gray-300 dark:border-gray-700"
                : "",
            className,
          )}
        >
          {icon && (
            <div
              className={cn(
                "absolute left-3 pointer-events-none",
                disabled ? "text-gray-300 dark:text-gray-600" : "text-gray-500 dark:text-gray-400",
              )}
            >
              {icon}
            </div>
          )}

          <input
            ref={ref}
            className={cn(
              variant === "underline"
                ? "w-full text-[13px] font-normal outline-none bg-transparent font-inter placeholder:font-normal placeholder:text-gray-500 dark:placeholder:text-gray-400 pr-10"
                : "w-full text-[12.5px] font-normal outline-none bg-transparent placeholder:text-[0.8375rem] placeholder:text-gray-500 dark:placeholder:text-gray-400",
              rightIcon ? "pr-24" : "pr-6",
              disabled ? "cursor-not-allowed text-gray-400 dark:text-gray-500" : "text-gray-800 dark:text-gray-100",
              inputClassName,
            )}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            type={isPassword ? (showPassword ? "text" : "password") : type}
            disabled={disabled}
            value={isControlled ? value : undefined}
            defaultValue={defaultValue}
            required={required}
            name={name}
            readOnly={!editable}
            {...otherRest}
          />
        </div>

        {/* Right icons */}
        <div
          className={cn(
            "absolute flex items-center space-x-1",
            variant === "underline"
              ? "right-2 top-3"
              : "right-3 top-1/2 -translate-y-1/2"
          )}
        >
          {shouldShowRightIcon && rightIcon && (
            <div className={cn("text-gray-500", disabled && "text-gray-300")}>
              {rightIcon}
            </div>
          )}
          {!rightIcon && isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center focus:outline-none"
              disabled={disabled}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
          {EmailValidationIcon()}
        </div>
      </div>
    );
  },
);

CommonInput.displayName = "CommonInput";

export default CommonInput;
